import {Box, Button, Typography} from '@mui/material';
import React, {useCallback, useMemo} from 'react';
import {
  VictoryArea,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import {
  smallAxisStyleAxis,
  smallAxisStyleLabel,
  smallAxisStyleTickLabels,
  smallAxisStyleTicks,
  toolTipStyle,
} from '../../services/graphService';
import GraphLegendItem from './GraphLegendItem';
import {useTranslation} from 'react-i18next';
import {LIGHT_GRAY, ORANGE_COLOR} from '../../styles';
import {useResizeDetector} from 'react-resize-detector';
import {DateTime} from 'luxon';
import {DefaultTFuncReturn} from 'i18next';
import {downloadReportCsv} from '../../services/reportService';

const CHRONIC_LOAD_COLOR = `${LIGHT_GRAY}80`;
const ACUTE_LOAD_COLOR = `${ORANGE_COLOR}AA`;
const ACWR_COLOR = '#fff';

interface Props {
  loadHistory: {
    date: string;
    acuteWorkload: number | null;
    chronicWorkload: number | null;
    acwr: number | null;
  }[];
  title?: string | DefaultTFuncReturn;
  height?: number;
  showLegend?: boolean;
  showDownloadButton?: boolean;
  teamName?: string;
  reportName?: string;
  xAxisTickPeriod?: number;
}

const LoadGraph: React.FC<Props> = ({
  loadHistory,
  title,
  height = 300,
  showLegend = true,
  showDownloadButton,
  teamName,
  reportName,
  xAxisTickPeriod = 7,
}) => {
  const {t} = useTranslation();

  const {width, ref} = useResizeDetector();

  const getTooltipText = useCallback(
    (date: string) => {
      const load = loadHistory.find(load => load.date === date);
      const tooltipSegments = [
        `${DateTime.fromISO(date).toFormat('M/d')}`,
        `${t('general.acute')}: ${(load?.acuteWorkload ?? 0).toFixed(0)}`,
        `${t('general.chronic')}: ${(load?.chronicWorkload ?? 0).toFixed(0)}`,
        `${t('general.ratio')}: ${(load?.acwr ?? 0).toFixed(1)}`,
      ];
      return tooltipSegments.join('\n');
    },
    [loadHistory, t],
  );

  const onDownload = useCallback(() => {
    const flatData = loadHistory.map(day => ({
      Date: day.date,
      'Acute Load Score': day.acuteWorkload,
      'Chronic Load Score': day.chronicWorkload,
      ACWR: day.acwr,
    }));
    downloadReportCsv({
      exportReportData: flatData,
      teamName,
      reportName: reportName ?? 'load',
    });
  }, [loadHistory, reportName, teamName]);

  const acwrScaleFactor = useMemo(() => {
    const largestWorkload = [
      ...(loadHistory ?? []).map(load => load.acuteWorkload ?? 0),
      ...(loadHistory ?? []).map(load => load.chronicWorkload ?? 0),
    ].sort((a, b) => b - a)[0];

    const largestAcwr = [...(loadHistory ?? [])]
      .map(load => load.acwr ?? 0)
      .sort((a, b) => b - a)[0];

    if (largestWorkload === 0) {
      return 1;
    }

    return largestAcwr / largestWorkload;
  }, [loadHistory]);

  // Check if all load score values are 0
  const allLoadScoresZero = useMemo(() => {
    return (
      loadHistory.length > 0 &&
      loadHistory.every(
        load =>
          (load.acuteWorkload ?? 0) === 0 && (load.chronicWorkload ?? 0) === 0,
      )
    );
  }, [loadHistory]);

  // Check if all ACWR values are 0
  const allAcwrZero = useMemo(() => {
    return (
      loadHistory.length > 0 &&
      loadHistory.every(load => (load.acwr ?? 0) === 0)
    );
  }, [loadHistory]);

  return (
    <Box sx={{width: '100%'}} ref={ref}>
      {(title || showDownloadButton) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}>
          {title && <Typography variant="h3">{title}</Typography>}
          {showDownloadButton && (
            <Button onClick={onDownload}>{t('general.download')}</Button>
          )}
        </Box>
      )}
      <VictoryChart
        containerComponent={
          <VictoryVoronoiContainer
            voronoiDimension="x"
            labels={({datum}: {datum: {x: string; y: number}}) => {
              return getTooltipText(datum.x);
            }}
            labelComponent={
              <VictoryTooltip style={toolTipStyle} constrainToVisibleArea />
            }
            voronoiBlacklist={['acute', 'acwr-line', 'chronic']}
          />
        }
        width={width ?? undefined}
        height={height}
        domainPadding={{x: 20}}
        padding={{top: 10, bottom: 40, left: 70, right: 60}}>
        <VictoryArea
          name="chronic"
          height={height}
          data={loadHistory.map(load => ({
            x: load.date,
            y: load.chronicWorkload,
          }))}
          style={{
            data: {
              fill: CHRONIC_LOAD_COLOR,
            },
          }}
        />
        <VictoryBar
          name="acute"
          height={height}
          barRatio={0.7}
          cornerRadius={{top: 5, bottom: 5}}
          data={loadHistory.map(load => ({
            x: load.date,
            y: load.acuteWorkload,
          }))}
          style={{
            data: {
              fill: ACUTE_LOAD_COLOR,
            },
          }}
        />
        <VictoryLine
          name="acwr-line"
          height={height}
          data={loadHistory.map(load => ({
            x: load.date,
            y: (load.acwr ?? 0) / acwrScaleFactor,
          }))}
          style={{
            data: {
              stroke: ACWR_COLOR,
              strokeWidth: 2,
            },
          }}
        />
        <VictoryScatter
          name="acwr-scatter"
          height={height}
          data={loadHistory.map(load => ({
            x: load.date,
            y: (load.acwr ?? 0) / acwrScaleFactor,
          }))}
          size={2}
          style={{
            data: {
              stroke: ACWR_COLOR,
              strokeWidth: 2,
              fill: ACWR_COLOR,
            },
          }}
        />
        <VictoryAxis
          tickValues={loadHistory.map((_, index) => index)}
          tickFormat={(tick, index) =>
            index % xAxisTickPeriod === 0
              ? DateTime.fromISO(tick).toFormat('M/d')
              : null
          }
          style={{
            axis: smallAxisStyleAxis,
            ticks: smallAxisStyleTicks,
            tickLabels: smallAxisStyleTickLabels,
          }}
        />
        <VictoryAxis
          dependentAxis
          orientation="left"
          offsetX={70}
          label={t('statistics.loadScore')}
          tickValues={allLoadScoresZero ? [] : undefined}
          tickFormat={t => (t ?? 0).toFixed(0)}
          style={{
            axis: smallAxisStyleAxis,
            ticks: allLoadScoresZero
              ? {...smallAxisStyleTicks, stroke: 'none'}
              : smallAxisStyleTicks,
            tickLabels: allLoadScoresZero
              ? {...smallAxisStyleTickLabels, fill: 'transparent'}
              : smallAxisStyleTickLabels,
            axisLabel: smallAxisStyleLabel,
          }}
        />
        <VictoryAxis
          dependentAxis
          orientation="right"
          offsetX={70}
          label={t('statistics.loadRatio')}
          tickValues={allAcwrZero ? [] : undefined}
          tickFormat={t => (t * acwrScaleFactor).toFixed(1)}
          style={{
            axis: smallAxisStyleAxis,
            ticks: allAcwrZero
              ? {...smallAxisStyleTicks, stroke: 'none'}
              : smallAxisStyleTicks,
            tickLabels: allAcwrZero
              ? {...smallAxisStyleTickLabels, fill: 'transparent'}
              : smallAxisStyleTickLabels,
            axisLabel: smallAxisStyleLabel,
          }}
        />
      </VictoryChart>
      {showLegend && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}>
          <Box sx={{display: 'flex', flexDirection: 'row'}}>
            <GraphLegendItem
              backgroundColor={CHRONIC_LOAD_COLOR}
              label={t('statistics.chronicLoad')}
              sx={{mr: 2}}
            />
            <GraphLegendItem
              backgroundColor={ACUTE_LOAD_COLOR}
              label={t('statistics.acuteLoad')}
              sx={{mr: 2}}
            />
            <GraphLegendItem
              backgroundColor={ACWR_COLOR}
              label={t('statistics.loadRatio')}
              sx={{mr: 2}}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LoadGraph;
